import { COLLECTION_LAYER_ID } from '@utils/constants';

/**
 * Sidebar IDs used by the Collection Results feature.
 * Shared between CollectionResults (triggers), CollectionFilter, and ObjectFilter.
 *
 * The hexagon and DSP ids are `collection`-prefixed because the model path uses
 * the bare 'hexagonColorFilter'/'dspColorFilter' (see LayerView's own
 * `sidebarIds`) and LayerView renders both stacks at once: SidebarTemplate
 * matches on the id string alone against one global `activeSidebars`, so a
 * shared id would open both panels together and let one stack's controls drive
 * the other's layer.
 */
export const COLLECTION_SIDEBAR_IDS = {
	collectionFilter: 'collectionFilter',
	objectFilter: 'objectFilter',
	hexagonPanel: 'collectionHexagonPanel',
	hexagonSettings: 'collectionHexagonSettings',
	hexagonColorFilter: 'collectionHexagonColorFilter',
	dspPanel: 'collectionDspPanel',
	dspColorFilter: 'collectionDspColorFilter',
};

/**
 * Fallback currency for the hexagon color-filter's cost UI, matching the
 * model path's "no currency selected yet" default. Used consistently by
 * CollectionHexagonColorFilter (the resolved `currency` passed down) and its
 * children's own prop defaults, so a currency can never resolve to
 * `undefined` regardless of which component computes it first.
 */
export const DEFAULT_CURRENCY = { id: 'UNIT', symbol: '', label: 'Units' };

/**
 * Hexagon cell-size (m²) input bounds; both ends also guard
 * `readHexagonConfig`. Matches the model path's HexagonSize control.
 */
export const HEXAGON_RESOLUTION_MIN = 1;
export const HEXAGON_RESOLUTION_MAX = 10000;
export const HEXAGON_RESOLUTION_STEP = 1;

/**
 * The color an object falls back to when its stored `style.color` is absent or
 * in a form `toHexColor` can't read. Shared with the map's style builder so the
 * tree's swatch and the rendered detections can't disagree about it.
 */
export const FALLBACK_OBJECT_COLOR = '#ff0000';

/** Opacity of a tree row whose object is unticked, i.e. hidden from the map. */
export const HIDDEN_ROW_OPACITY = 0.55;

/**
 * Tint for a filter control with filters narrowing its scope — header and rows
 * must agree. antd runs with `cssVar`, so this needs no `useToken()` per row.
 */
export const ACTIVE_FILTER_STYLE = Object.freeze({ color: 'var(--ant-color-primary)' });

/** Shared preset palette — object styling and the hexagon layer's color picker. */
export { OBJECT_COLOR_PALETTE } from '@utils/constants';

/**
 * How a detected object is drawn on the map. Values match the backend verbatim:
 * the taxonomy's per-node `detection_pointer` recommendation
 * (`GET /v2/taxonomy/nodes`) and the user's per-object override, persisted at
 * `detection_objects[].style.detection_pointer` on the collection.
 *
 * Sharing one vocabulary with the backend means these strings travel from the
 * API through the picker to the map layer keys with no translation step.
 */
export const DETECTION_POINTER = Object.freeze({
	CENTER_POINT: 'center_point',
	BOUNDING_BOX: 'bounding_box',
	OUTLINE: 'outline',
});

/**
 * Used when the taxonomy has no recommendation for an object (an unknown or
 * inactive node). Center points are the cheapest shape to render and always
 * available, so this is the safest last resort.
 */
export const DEFAULT_DETECTION_POINTER = DETECTION_POINTER.CENTER_POINT;

/** Human labels for the detection-pointer picker. */
export const DETECTION_POINTER_LABELS = Object.freeze({
	[DETECTION_POINTER.CENTER_POINT]: 'Center point',
	[DETECTION_POINTER.BOUNDING_BOX]: 'Bounding box',
	[DETECTION_POINTER.OUTLINE]: 'Outline',
});

/**
 * Every pointer, in the order the picker lists them. Anything that needs one
 * entry per shape — layer maps, style maps, availability counts — should derive
 * from this rather than spelling the three out again.
 */
export const DETECTION_POINTERS = Object.freeze(Object.keys(DETECTION_POINTER_LABELS));

/**
 * Map layer id per detection pointer, derived from the canonical
 * `COLLECTION_LAYER_ID` in @utils/constants rather than restating the strings.
 *
 * Three separate places have to agree on these ids: CollectionMapLayer registers
 * the layers under them, useCollectionTooltip restricts hit-testing to them, and
 * the AOI draw flow hides them by way of COLLECTION_RESULT_LAYER_IDS. A second
 * copy of the literals meant a rename could satisfy the compiler, the tests and
 * the reviewer while silently killing hover and leaving detections on screen
 * underneath a polygon being sketched.
 *
 * Named for what it maps *from*: a near-twin of `COLLECTION_LAYER_ID` differing
 * only by a trailing `S` is too easy to reach for by mistake.
 */
export const DETECTION_POINTER_LAYER_ID = Object.freeze({
	[DETECTION_POINTER.CENTER_POINT]: COLLECTION_LAYER_ID.CENTER,
	[DETECTION_POINTER.BOUNDING_BOX]: COLLECTION_LAYER_ID.BOUNDING_BOX,
	[DETECTION_POINTER.OUTLINE]: COLLECTION_LAYER_ID.OUTLINE,
});
